import { redirect } from "@tanstack/react-router";
import type { CadernoListResponse } from "../api/caderno";
import { cadernosOptions } from "../queries/caderno";
import { onboardingOptions } from "../queries/onboarding";
import { landingVariant, trackLandingExperiment } from "./experiment";
import { queryClient } from "./query-client";

/**
 * The ONE onboarding redirect left in Lume.
 *
 * There used to be seven guards — four on a caderno's screens, three on the
 * hub's — each of which could send a student to a walkthrough instead of the
 * page they asked for. They are gone. A caderno's walkthrough is now six
 * tooltips mounted over the real Painel (components/onboarding/CadernoTour), so
 * it needs no route and no redirect, and the hub's tours were deleted outright.
 *
 * What is left is the welcome: a student with no caderno has nothing to be shown
 * around, so the hub's home hands them to a screen whose only job is to start
 * one.
 *
 * A ROUTE GUARD, and it has to be. As an effect inside Início it could only fire
 * after the screen had mounted and rendered — the student would see the hub, then
 * be moved. Deciding in `beforeLoad` means the answer is known before the route
 * commits, identical on a first navigation and on a reload.
 *
 * IT IS BOUNDED. `beforeLoad` holds the navigation until it resolves, so a slow
 * endpoint would otherwise hold the app — including the QueryClient's own retries
 * on a 5xx, which are seconds of backoff. Past the budget the route proceeds and
 * the student simply gets Início, which is a fine screen to be on.
 *
 * ONE MORE REDIRECT RIDES ON THE SAME READS: the landing test
 * (lume_landing_tutor_v1), which sends half the browsers opening /lume/ to the
 * Tutor instead. It is decided here, after the welcome and only when the welcome
 * did not fire, because it needs the very cadernos page the welcome already
 * waited for — see decideLanding.
 */

/**
 * How long a navigation may wait on the reads before going ahead without them.
 * Generous enough for an ordinary round trip, short enough that a dead endpoint
 * costs a beat rather than the screen.
 */
const GUARD_BUDGET_MS = 1500;

function timeout(ms: number): Promise<undefined> {
  return new Promise((resolve) => setTimeout(() => resolve(undefined), ms));
}

/**
 * Sends a student who has never been welcomed, and has no caderno, to /bem-vindo.
 *
 * ON INÍCIO AND NOWHERE ELSE — exactly `/lume/`, not `/cadernos` and not a tool.
 * The hub's old walkthroughs fired on the shell, so anyone landing deeper got a
 * tour of tabs before the screen they came for; a student who typed a deeper URL
 * wanted that URL.
 *
 * TWO CONDITIONS, and the second is the interesting one: the welcome is only true
 * for someone with nothing yet, so it reads the cadernos list as well as the
 * seen-flag. That read is not extra work — Início fetches the same page one
 * render later and finds it in the cache.
 *
 * The flag is what stops it coming back for a student who dismissed it and still
 * has no caderno (every way out of the welcome marks it seen, cache first — see
 * useMarkOnboardingSeen). `fromWelcome` is the belt to that braces, for the one
 * case the cache write cannot cover: a student who opened /bem-vindo directly,
 * before the status had ever been read, leaves nothing for the optimistic write
 * to update and would be sent straight back while the POST was in flight.
 */
export async function guardWelcome(options: {
  /** True when the welcome itself is handing the student over — see above */
  fromWelcome: boolean;
  /** This is the tab's first screen — see claimLanding */
  isLanding: boolean;
  /** True on a link-hover preload: nothing here may be reported */
  preload: boolean;
}): Promise<void> {
  if (options.fromWelcome || options.preload) {
    return;
  }

  let resolved;

  try {
    resolved = await Promise.race([
      Promise.all([
        queryClient.ensureQueryData(onboardingOptions()),
        queryClient.ensureQueryData(cadernosOptions(1)),
      ]),
      timeout(GUARD_BUDGET_MS),
    ]);
  } catch {
    return;
  }

  // The budget ran out — not an error, just not now
  if (resolved === undefined) {
    return;
  }

  const [status, cadernos] = resolved;

  if (!status.hub_welcome && cadernos.total === 0) {
    throw redirect({ to: "/bem-vindo", replace: true });
  }

  if (options.isLanding) {
    decideLanding(cadernos);
  }
}

/**
 * Set once this browser session has had its landing decided, whichever arm.
 * Session-scoped so a new visit decides again, and per tab like sessionStorage
 * itself.
 */
const LANDING_DECIDED_KEY = "lume.landingDecided";

/**
 * Claims this session's one landing decision, on the tab's first authenticated
 * navigation (router.tsx). False when it was already claimed — or when storage
 * throws, since a decision that cannot be remembered would be made again on every
 * visit to Início and bounce the student each time.
 */
export function claimLanding(): boolean {
  try {
    if (window.sessionStorage.getItem(LANDING_DECIDED_KEY) !== null) {
      return false;
    }

    window.sessionStorage.setItem(LANDING_DECIDED_KEY, "1");

    return true;
  } catch {
    return false;
  }
}

/**
 * The landing test: arm `b` opens on the Tutor rather than on Início.
 *
 * ONCE PER SESSION, and only when the tab's first screen after login is /lume/.
 * The claim is spent in both arms, so tapping "Início" afterwards always shows
 * Início. A student sent to the welcome, or whose reads missed the budget, spends
 * it without being exposed.
 *
 * The Tutor is the caderno's when there is one — items[0], the most recently
 * opened (Caderno orders by last_opened_at) — and the hub's otherwise.
 *
 * QA: ?lt=b forces the arm, but only in a tab that has not decided yet — a fresh
 * one, since the flag lives in sessionStorage.
 */
function decideLanding(cadernos: CadernoListResponse): void {
  trackLandingExperiment();

  if (landingVariant() !== "b") {
    return;
  }

  const latest = cadernos.items[0];

  if (latest) {
    throw redirect({
      to: "/caderno/$cadernoId/chat",
      params: { cadernoId: String(latest.id) },
      replace: true,
    });
  }

  throw redirect({ to: "/chat", replace: true });
}

/**
 * Whether this navigation is the welcome handing the student over.
 *
 * The flag rides in history state rather than in the URL: it describes HOW the
 * student got here, not where they are, so it must not survive a copy-paste or a
 * reload — and on a reload the right answer is genuinely "they chose this
 * screen".
 *
 * Typed loosely because history state is: TanStack widens it to whatever the app
 * puts there, and one boolean is not worth a module augmentation.
 */
export function cameFromWelcome(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { fromWelcome?: unknown }).fromWelcome === true
  );
}
